import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AppIcon,
  BriefcaseBusinessIcon,
  CircleCheckIcon,
  SparklesIcon,
  XIcon,
} from '../../components/icons';
import { apiClient } from '../../shared/services/api';
import { saveLocalReferrerUpgradeRequest } from '../../shared/utils/referrerUpgradeStorage';
import { SuccessStoryStep, type SuccessStoryPayload } from './SuccessStoryStep';
import { ShareCardStep } from './ShareCardStep';
import './HiredUpgradeModal.css';

const CORPORATE_JOB_IMAGE = '/images/illustration-depicting-corporate-job.png';

type ModalStep = 'form' | 'story' | 'share';

interface HiredUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function HiredUpgradeModal({ isOpen, onClose, onSubmitted }: HiredUpgradeModalProps) {
  const [step, setStep] = useState<ModalStep>('form');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [upgradeRequestId, setUpgradeRequestId] = useState<number | null>(null);
  const [storySubmitting, setStorySubmitting] = useState(false);
  const [storyError, setStoryError] = useState('');
  const [shareCompanyName, setShareCompanyName] = useState('');
  const [shareRoleTitle, setShareRoleTitle] = useState('');

  if (!isOpen) return null;

  const resetAndClose = () => {
    setStep('form');
    setCompanyName('');
    setCompanyEmail('');
    setError('');
    setSubmitting(false);
    setUpgradeRequestId(null);
    setStorySubmitting(false);
    setStoryError('');
    setShareCompanyName('');
    setShareRoleTitle('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const name = companyName.trim();
    const email = companyEmail.trim();

    if (!name && !email) {
      setError('Please enter your company name and your company mail id.');
      return;
    }
    if (!name) {
      setError('Please enter your company name.');
      return;
    }
    if (!email) {
      setError('Please enter your company mail id.');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError('Please enter a valid company mail id.');
      return;
    }

    setSubmitting(true);
    const result = await apiClient.createReferrerUpgradeRequest({
      company_name: name,
      company_email: email,
    });
    setSubmitting(false);
    const requestId = result.data?.id ?? null;
    setUpgradeRequestId(requestId);
    setShareCompanyName(name);
    saveLocalReferrerUpgradeRequest(name, email);
    setStep('story');
    onSubmitted();
  };

  const handleStorySubmit = async (payload: SuccessStoryPayload) => {
    if (!upgradeRequestId) {
      setStep('share');
      return;
    }
    setStorySubmitting(true);
    setStoryError('');
    try {
      await apiClient.createSuccessStory({
        upgrade_request_id: upgradeRequestId,
        referrer_id: payload.referrer_id,
        company_name: shareCompanyName,
        role_title: payload.role_title,
        quote: payload.quote,
        auto_generated_quote: payload.auto_generated_quote,
      });
      setShareRoleTitle(payload.role_title);
    } catch (err: any) {
      // Non-blocking: log but advance to share step regardless
      console.error('Failed to save success story:', err);
      setStoryError('Could not save your story, but you can still share!');
      setShareRoleTitle(payload.role_title);
    } finally {
      setStorySubmitting(false);
      setStep('share');
    }
  };

  return createPortal(
    <div className="hired-modal-backdrop" role="presentation" onClick={resetAndClose}>
      <div
        className="hired-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hired-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="hired-modal__aurora" aria-hidden />

        <button type="button" className="hired-modal__close" onClick={resetAndClose} aria-label="Close">
          <AppIcon icon={XIcon} size={16} />
        </button>

        <div className="hired-modal__layout">
          <div className="hired-modal__visual">
            <span className="hired-modal__visual-glow" aria-hidden />

            {step === 'form' || step === 'story' ? (
              <>
                <span className="hired-modal__visual-badge">
                  <AppIcon icon={SparklesIcon} size={12} />
                  Career milestone
                </span>
                <div className="hired-modal__image-frame">
                  <img
                    src={CORPORATE_JOB_IMAGE}
                    alt="Corporate job celebration"
                    className="hired-modal__image"
                    width={400}
                    height={400}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </>
            ) : (
              <div className="hired-modal__success-visual">
                <span className="hired-modal__success-icon" aria-hidden>
                  <AppIcon icon={CircleCheckIcon} size={34} />
                </span>
                <p className="hired-modal__success-caption">
                  Your upgrade request is on its way to the ReffConnect team.
                </p>
              </div>
            )}
          </div>

          <div className="hired-modal__content">
            {step === 'form' ? (
              <form className="hired-modal__form" onSubmit={handleSubmit} noValidate>
                <div className="hired-modal__scroll">
                  <div className="hired-modal__head">
                    <span className="hired-modal__head-icon" aria-hidden>
                      <AppIcon icon={BriefcaseBusinessIcon} size={18} />
                    </span>
                    <div>
                      <span className="hired-modal__eyebrow">Working Professional upgrade</span>
                      <h2 id="hired-modal-title" className="hired-modal__title">
                        Congratulations!
                      </h2>
                    </div>
                  </div>

                  <p className="hired-modal__text">
                    Congratulations on your new role! Now unlock your{' '}
                    <strong>Working Professional Dashboard</strong> on <strong>ReffConnect</strong> to offer
                    referrals, resume reviews, culture calls, and more to job seekers. We are so excited to
                    see you as a working professional.
                  </p>
                  <p className="hired-modal__subtext">
                    Enter the company that hired you below. The ReffConnect team will verify your details and
                    upgrade your account.
                  </p>

                  <div className="hired-modal__fields">
                    <div className="hired-modal__field">
                      <label className="hired-modal__label" htmlFor="hired-company-name">
                        Company name <span className="hired-modal__required">*</span>
                      </label>
                      <input
                        id="hired-company-name"
                        type="text"
                        className="hired-modal__input"
                        placeholder="e.g. Google, TCS, Infosys"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        disabled={submitting}
                        autoComplete="organization"
                      />
                    </div>

                    <div className="hired-modal__field">
                      <label className="hired-modal__label" htmlFor="hired-company-email">
                        Company email ID <span className="hired-modal__required">*</span>
                      </label>
                      <input
                        id="hired-company-email"
                        type="email"
                        className="hired-modal__input"
                        placeholder="you@company.com"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        disabled={submitting}
                        autoComplete="work email"
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="hired-modal__error" role="alert">
                      {error}
                    </p>
                  )}
                </div>

                <div className="hired-modal__actions">
                  <button
                    type="button"
                    className="hired-modal__btn hired-modal__btn--ghost"
                    onClick={resetAndClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="hired-modal__btn hired-modal__btn--primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                    {!submitting && <span className="hired-modal__btn-shine" aria-hidden />}
                  </button>
                </div>
              </form>
            ) : step === 'story' ? (
              <div className="hired-modal__panel">
                {storyError && (
                  <p className="hired-modal__error hired-modal__error--info" role="status">
                    {storyError}
                  </p>
                )}
                <SuccessStoryStep
                  upgradeRequestId={upgradeRequestId ?? 0}
                  companyName={shareCompanyName}
                  onSubmit={handleStorySubmit}
                  onSkip={() => setStep('share')}
                  submitting={storySubmitting}
                />
              </div>
            ) : (
              <ShareCardStep
                companyName={shareCompanyName}
                roleTitle={shareRoleTitle || 'your new role'}
                onDone={resetAndClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
